<script setup lang="ts">
import { ModalCenterDesktopSize, lockClass } from '~/lib/models/ui/confirmation'

const props = withDefaults(
  defineProps<{
    mobileFull?: boolean
    desktopSize?: ModalCenterDesktopSize
    contentFull?: boolean
    noActionsClass?: boolean
    addCloseButton?: boolean
    contentOverflow?: boolean
    customMaxHeightContent?: string
  }>(),
  {
    mobileFull: true,
    desktopSize: 'medium',
    contentFull: false,
    noActionsClass: false,
    addCloseButton: true,
    contentOverflow: false,
    customMaxHeightContent: undefined,
  }
)

const emit = defineEmits(['close'])
const isMobile = useIsMobile()

const scroll = ref(0)

const isMobileFull = computed(() => props.mobileFull && isMobile?.value)
const isDesktopLarge = computed(() => props.desktopSize === 'large')
const isDesktopSmall = computed(() => props.desktopSize === 'small')

/* TODO - may be abstracted to composable or helper or react on any global modal open do this */
onMounted(() => {
  if (typeof window !== 'undefined') {
    scroll.value = window.scrollY
    const root = document.getElementsByTagName('html')[0]
    root.classList.add(lockClass)
  }
})
onUnmounted(() => {
  if (typeof window !== 'undefined') {
    const root = document.getElementsByTagName('html')[0]
    root.classList.remove(lockClass)
    window.scrollTo(0, scroll.value)
  }
})

const contentClass = computed(() => {
  if (props.contentFull) {
    return 'flex flex-col flex-1 overflow-y-auto '
  } else {
    return `mcd-custom-scrollbar h-[calc(100%-(64px+64px))] overflow-y-auto md:h-fit md:max-h-[75vh] ${
      isMobile?.value && !isMobileFull.value ? 'max-h-[70vh]' : ''
    }`
  }
})

const actionsClass = computed(() => {
  if (props.noActionsClass) return ''
  if (props.contentFull) {
    return 'border-sm'
  } else {
    return 'flex h-[64px] w-[100vw] flex-row items-center md:mx-auto md:h-fit md:w-[80%] md:justify-between md:p-8'
  }
})

const customHeightContent = computed(() => {
  return props.customMaxHeightContent ? props.customMaxHeightContent : ''
})
</script>

<template>
  <div
    data-test="mcd-modal-container"
    class="fixed left-0 top-0 z-10 flex min-h-full w-full flex-col justify-end bg-black/50 backdrop-blur md:items-center md:justify-center"
  >
    <div
      data-test="mcd-modal-center__body"
      class="relative flex max-h-[100vh] w-full flex-col rounded-lg bg-white text-mcd-black shadow-lg md:max-h-[80svh] md:max-w-[800px]"
      :class="{
        'h-screen': contentFull, // || isMobileFull maybe too to fill full height? when mobileFull and !contentFull
        'md:max-w-[768px] lg:max-w-[900px]': isDesktopLarge,
        'md:!max-w-[472px]': isDesktopSmall,
        'rounded-none': isMobile && contentFull,
        'rounded-b-none rounded-t-[20px]': isMobile && !contentFull,
        'overflow-hidden': isMobile && !contentOverflow,
        [customHeightContent]: customHeightContent,
      }"
    >
      <CloseIcon
        v-if="addCloseButton"
        class="fixed left-6 top-6 h-auto w-4"
        :class="{ 'absolute left-auto right-6': !isMobile || isMobileFull }"
        :fill="isMobile && !isMobileFull ? 'white' : undefined"
        @click="emit('close')"
      />
      <!-- Header -->
      <div
        data-test="mcd-modal-center__body__header"
        class="flex flex-col justify-center px-2 pb-2 pt-3 text-lg font-bold md:px-14 md:pb-0 md:pt-8 md:text-2xl"
      >
        <slot name="header"></slot>
      </div>
      <!-- Content -->
      <div data-test="mcd-modal-center__body__content" :class="contentClass">
        <slot name="content"></slot>
      </div>
      <!-- Actions -->
      <div data-test="mcd-modal-center__body__actions" :class="actionsClass">
        <slot name="actions"></slot>
      </div>
    </div>
    <ToastContainer />
  </div>
</template>
